import React, { useEffect } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';
import { SwitchTransition, Transition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/pro-light-svg-icons';

import routes from 'js/core_pre_webpack/routes';
import ModalBase from 'js/components/modals/Modal';
import { TransitionState, transitions } from './fadeTransition';
import LoadingDots from './LoadingDots';
import formatNumber from 'js/utils/format';
import {
  recordShow,
  recordDismissal,
  recordClickthrough,
} from 'js/utils/dismissibles';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { getSearchUpsellTracking } from './searchUpsellTracking';
import { getSearchUpsellTracking as getExperimentTracking } from './searchUpsellExperiment';

const ACADEMIA_BASIC_BLUE = '#48689F';
const ACADEMIA_PREMIUM_BLUE = '#0954D3';

type Props = {
  advancedCount: number | null;
  onClose: () => void;
  query: string;
  trackExperiment?: boolean;
  workCount: number | null;
};

const Bullets = styled.ul`
  margin: 0 0 32px;
  padding: 16px 0 0 48px;
`;

const BulletContainer = styled.li`
  align-items: center;
  display: flex;
  margin: 0;
  padding: 0;

  &:not(:last-of-type) {
    margin: 0 0 16px;
  }
`;

const BulletText = styled.p<{ strikethrough?: boolean }>`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #222222;
  margin: 0;

  &.strikethrough {
    color: #dfdfdf;
    text-decoration: line-through;
  }
`;

const Button = styled(ImpressionWatcher).attrs({ as: 'button' })<{
  primary?: boolean;
}>`
  align-items: center;
  background: #e6edf9;
  border: 0;
  border-radius: 5px;
  color: ${ACADEMIA_BASIC_BLUE};
  cursor: pointer;
  display: flex;
  flex-grow: 1;
  font-size: 17px;
  font-weight: 700;
  height: 54px;
  justify-content: center;

  &.primary {
    background: ${ACADEMIA_PREMIUM_BLUE};
    color: #fff;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 0;
  color: #7a7a7a;
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  right: 32px;
  top: 32px;
`;

const IconContainer = styled.div<{ blank?: boolean }>`
  background: #e6edfa;
  border-radius: 50%;
  color: #0954d3;
  display: grid;
  font-size: 16px;
  height: 30px;
  margin-right: 12px;
  place-items: center;
  width: 30px;

  &.blank {
    background: transparent;
  }
`;

const Modal = styled(ModalBase)`
  border-radius: 10px;
  max-height: unset;
  max-width: 870px;
  min-width: 870px;
  padding: 60px 18px;
  width: 85%;
`;

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  width: 460px;
  padding: 0 30px;

  &:first-of-type {
    border-right: 1px solid #ddd;
  }
`;

const PanelContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const PanelHeader = styled.h1<{ primary?: boolean }>`
  background: #e6edf9;
  border-bottom: 1px solid #ddd;
  color: ${ACADEMIA_BASIC_BLUE};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.5;
  padding: 16px 0;
  text-align: center;
  margin-top: 0px;

  &.primary {
    background: ${ACADEMIA_PREMIUM_BLUE};
    color: #fff;
  }
`;

const PanelSubtitle = styled.h2<{ strong?: boolean }>`
  align-items: center;
  color: #4b4b4b;
  display: flex;
  font-size: 18px;
  font-style: italic;
  justify-content: center;
  padding: 16px 0 0 0;
  margin: 12px 0 12px 0;
  // This prevents the height from jumping when we switch from
  // LoadingDots to the CountText
  min-height: 53px;

  &.strong {
    font-weight: 700;
  }
`;

const Subtitle = styled.h2`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 24px;
  margin: 0 0 28px;
  text-align: center;
  color: #222222;
`;

const Title = styled.h1`
  color: #222222;
  font-family: Roboto;
  font-style: normal;
  font-weight: bold;
  font-size: 32px;
  line-height: 37px;
  margin: 0 0 16px;
  text-align: center;
`;

const CountContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FADE_DURATION = 180;

const CountText = styled.div<{ primary?: boolean }>`
  transition: ${FADE_DURATION}ms opacity;
  font-family: Roboto;
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
  line-height: 48px;
  color: ${ACADEMIA_BASIC_BLUE};

  &.primary {
    color: ${ACADEMIA_PREMIUM_BLUE};
  }
`;

const CountSubText = styled.div`
  transition: ${FADE_DURATION}ms opacity;
  font-family: Roboto;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 16px;
  color: #757575;
`;

const Bullet = ({
  children,
  x,
}: {
  children: React.ReactNode;
  x?: boolean;
}) => (
  <BulletContainer>
    <IconContainer blank={Boolean(x)}>
      {x ? ' ' : <FontAwesomeIcon icon={faCheck} />}
    </IconContainer>
    <BulletText strikethrough={Boolean(x)}>{children}</BulletText>
  </BulletContainer>
);

const UpsellModalRedesign = ({
  advancedCount,
  onClose,
  query,
  trackExperiment = false,
  workCount,
}: Props) => {
  // On mount, record that we've shown this modal
  useEffect(() => {
    recordShow('AdvancedSearchUpsellModal');
  }, []);

  const loggedOnClose = () => {
    recordDismissal('AdvancedSearchUpsellModal');
    onClose();
  };

  return (
    <Modal onClose={loggedOnClose}>
      <Title>Let us do the digging for you</Title>
      <Subtitle>
        Transform your research with advanced search that finds the papers you
        need, faster.
      </Subtitle>

      <PanelContainer>
        <Panel>
          <PanelHeader>Academia Basic Search</PanelHeader>
          <PanelSubtitle>
            <SwitchTransition>
              <Transition
                key={(workCount == null).toString()}
                timeout={FADE_DURATION}
              >
                {(state: TransitionState) =>
                  workCount != null ? (
                    <CountContainer>
                      <CountText style={transitions[state]}>
                        {formatNumber(workCount)}{' '}
                      </CountText>
                      <CountSubText>
                        {pluralize('result', workCount)} for ‘{query}’
                      </CountSubText>
                    </CountContainer>
                  ) : (
                    <LoadingDots
                      size={10}
                      // Have to put the transition in the style here
                      // since the way LoadingDots is written, using styled(LoadingDots)
                      // affects the individual dots, not the container
                      style={{
                        ...transitions[state],
                        transition: `${FADE_DURATION}ms opacity`,
                      }}
                    />
                  )
                }
              </Transition>
            </SwitchTransition>
          </PanelSubtitle>

          <Bullets>
            <Bullet>Search paper titles</Bullet>
            <Bullet x>Search full text of papers</Bullet>
            <Bullet x>Search papers in other languages</Bullet>
            <Bullet x>Translate your papers</Bullet>
            <Bullet x>Filter by publication type, date, and language</Bullet>
          </Bullets>

          <Button
            {...getSearchUpsellTracking(
              'advanced_search',
              'search_modal',
              'advanced-search-upsell-modal',
            )}
            active={false}
            onClick={loggedOnClose}
          >
            Keep it basic ▸
          </Button>
        </Panel>

        <Panel>
          <PanelHeader primary>Academia Advanced Search</PanelHeader>
          <PanelSubtitle strong>
            <SwitchTransition>
              <Transition
                key={(advancedCount == null).toString()}
                timeout={FADE_DURATION}
              >
                {(state: TransitionState) =>
                  advancedCount != null ? (
                    <CountContainer>
                      <CountText primary style={transitions[state]}>
                        {formatNumber(advancedCount)}{' '}
                      </CountText>
                      <CountSubText>
                        {pluralize('result', advancedCount)} for ‘{query}’
                      </CountSubText>
                    </CountContainer>
                  ) : (
                    <LoadingDots
                      size={10}
                      // Have to put the transition in the style here
                      // since the way LoadingDots is written, using styled(LoadingDots)
                      // affects the individual dots, not the container
                      style={{
                        ...transitions[state],
                        transition: `${FADE_DURATION}ms opacity`,
                      }}
                    />
                  )
                }
              </Transition>
            </SwitchTransition>
          </PanelSubtitle>

          <Bullets>
            <Bullet>Search paper titles</Bullet>
            <Bullet>Search full text of papers</Bullet>
            <Bullet>Search papers in other languages</Bullet>
            <Bullet>Translate your papers</Bullet>
            <Bullet>Filter by publication type, date, and language</Bullet>
          </Bullets>

          <Button
            {...getSearchUpsellTracking(
              'advanced_search',
              'search_modal',
              'advanced-search-upsell-modal',
            )}
            additionalTracking={
              trackExperiment
                ? {
                    ...getExperimentTracking(
                      'advanced_search',
                      'search_modal',
                      'advanced-search-upsell-modal',
                    ),
                    noBatch: true,
                  }
                : undefined
            }
            onClick={() => {
              recordClickthrough('AdvancedSearchUpsellModal');
              window.location.href = routes.checkout_path({
                feature: 'ADVANCED_SEARCH',
                trigger: 'advanced-search-upsell-modal',
                upgradeRedirect: '%2Fsearch%2Fadvanced',
                btv: 'search_default',
              });
            }}
            primary
          >
            Upgrade to see full results ▸
          </Button>
        </Panel>
      </PanelContainer>

      <CloseButton onClick={loggedOnClose}>
        <FontAwesomeIcon icon={faTimes} />
      </CloseButton>
    </Modal>
  );
};

export default UpsellModalRedesign;
